import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import { Input } from '../components/ui/input';
import { Leaf, Shield, Key, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetAdminSecretPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [newSecret, setNewSecret] = useState('');
  const [confirmSecret, setConfirmSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newSecret !== confirmSecret) {
      toast.error('Secrets do not match');
      return;
    }
    
    if (newSecret.length < 8) {
      toast.error('Secret must be at least 8 characters');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/admin/reset-secret`, {
        token: token,
        new_secret: newSecret
      });
      setResetSuccess(true);
      toast.success('Admin secret reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset secret');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <header className="border-b border-border bg-white/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Logo size="default" linkTo="/" />
          </div>
        </header>

        <main className="max-w-md mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm border border-border p-8"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-semibold text-[#1A1A1A] mb-2">Invalid Reset Link</h1>
              <p className="text-muted-foreground mb-6">
                This reset link is invalid or has expired. Please request a new reset link.
              </p>
              <Link to="/admin/forgot-secret">
                <Button className="btn-primary">
                  Request New Reset Link
                </Button>
              </Link>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="border-b border-border bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="default" linkTo="/" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-border p-8"
        >
          {resetSuccess ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-semibold text-[#1A1A1A] mb-2">Secret Reset Complete</h1>
              <p className="text-muted-foreground mb-6">
                Your admin secret has been successfully reset. You can now login with your new secret.
              </p>
              <Button 
                onClick={() => navigate('/admin')}
                className="w-full btn-primary"
              >
                Go to Admin Login
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Key className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-semibold text-[#1A1A1A]">Reset Admin Secret</h1>
                <p className="text-muted-foreground mt-2">
                  Enter your new admin secret below
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">New Secret</label>
                  <Input
                    type="password"
                    value={newSecret}
                    onChange={(e) => setNewSecret(e.target.value)}
                    placeholder="Enter new admin secret"
                    minLength={8}
                    required
                    data-testid="new-secret-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Must be at least 8 characters
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Confirm New Secret</label>
                  <Input
                    type="password"
                    value={confirmSecret}
                    onChange={(e) => setConfirmSecret(e.target.value)}
                    placeholder="Confirm new admin secret"
                    minLength={8}
                    required
                    data-testid="confirm-secret-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isLoading} 
                  className="w-full btn-primary"
                  data-testid="reset-secret-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Key className="w-4 h-4 mr-2" />
                  )}
                  {isLoading ? 'Resetting...' : 'Reset Admin Secret'}
                </Button>
              </form>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
